import { formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';
import { IMtaActions, IMtaFormNames, IMtaState, PageForm } from '../../../../../../../../services/mta/models';
import { IBrandingActions, II18nMessages } from '../../../../../../../../services/branding/models';
import * as React from 'react';
import { flow, forEach, get, keys, map, set, size, startCase } from 'lodash';
import { connect } from 'react-redux';
import { IStore } from '../../../../../../../../redux/IStore';
import { bindActionCreators } from 'redux';
import { mtaActions } from '../../../../../../../../services/mta/actions';
import { actions as brandingActions } from '../../../../../../../../services/branding/reducer';
import { Button, Form } from 'react-bootstrap';
import Loading from '../../../../../../../../components/Loading';
import { FaSpinner } from 'react-icons/fa';
import Signposting from '../../../../../Components/Signposting';
import { MTAButtons } from '../../../MTAButtons';
import { MTACardHeader } from '../Components/MTACardHeader';
import { push } from 'react-router-redux';
import { MTASchemeChoice } from '../../../MTASchemeChoice';

interface IMTAChangeTierFormFormProps extends InjectedFormProps<any> {
  mta: IMtaState;
  formValues: any;
  i18n: II18nMessages;
  selectedPolicy: any;
  removalReason: string;
  tripType: string;
  actions: {
    branding: IBrandingActions;
    mta: IMtaActions;
    push: (route: string) => void;
  };
}

class MTAChangeTierForm extends React.Component<IMTAChangeTierFormFormProps, any> {

  public state = {
    moreInfo: false,
  };

  constructor(props) {
    super(props);
    const { dispatch, change } = props;
    dispatch(change('page', PageForm.MTA_CHANGE_TIER));
    window.scrollTo(0, 0);
  }

  private standardiseTheName = (name) => startCase(name.replace('-', ' '));

  public handleSubmit = () => {
    const { actions } = this.props;

    Promise.all([
      actions.mta.calculateMtaPremiums(),
    ]).then(() => {
      actions.mta.addEndorsements();
      actions.mta.handleMTASubmit();
    });
  }

  private checkForReferrals = (selectedQuotes): boolean => {

    let hasReferral = false;

    forEach(selectedQuotes, (premium) => {
      if (get(premium, 'referrals', []).length > 0) {
        hasReferral = true;
      }
    });

    return hasReferral;

  }

  private onChange = (schemeId: string) => {
    const { change } = this.props
    change('data.attributes.scheme_id', schemeId);
  }

  private renderSchemeQuotes = (selectedTripTypeQuotes, tripTypeName) => {
    const { actions, formValues } = this.props;
    const product = actions.branding.getProduct();
    const schemeId = formValues.scheme_id;
    return (
      <MTASchemeChoice
        quotes={selectedTripTypeQuotes}
        product={product}
        tripType={tripTypeName}
        schemeId={schemeId}
        onSchemeChange={this.onChange}
      />
    )
  }

  private handleGoBack = () => {
    const { actions } = this.props;
    actions.mta.setCompleteMTA();
    actions.push(`/account/summary`)
  }

  render() {
    const {
      mta,
      actions,
      handleSubmit,
      formValues,
      tripType: tripTypeValue,
      submitting,
      submitFailed,
      valid
    } = this.props;
    const isLoading = mta.isLoading;
    const disableOnNoChange = get(mta, 'selectedPolicy.metadata.scheme_id', '') === get(formValues, 'scheme_id', '')
    const mtaQuotes = mta.quotes;
    const product = actions.branding.getProduct();
    const quotes = { admiral: null, gold: null, platinum: null };
    let tripTypeName = this.standardiseTheName(product.metadata.trip_type[tripTypeValue]);

    forEach(mtaQuotes, (quote: any) => {
      if (quote.scheme.cover_level === 'admiral') {
        set(quotes, `admiral.${quote.scheme.scheme_type}`, quote)
      }
      if (quote.scheme.cover_level === 'gold') {
        set(quotes, `gold.${quote.scheme.scheme_type}`, quote)
      }
      if (quote.scheme.cover_level === 'platinum') {
        set(quotes, `platinum.${quote.scheme.scheme_type}`, quote)
      }
    })

    const orderedQuotes = product.metadata.scheme_order.map((order) => {
      return quotes[order];
    });

    const selectedTripTypeQuotes = map(orderedQuotes[0] && orderedQuotes, (tripTypes) => {
      if (size(tripTypes) === 1) {
        tripTypeName = keys(tripTypes)[0];
      }
      return tripTypes[tripTypeName]
    })

    if (isLoading) {
      return <Loading/>
    }

    if (this.checkForReferrals(selectedTripTypeQuotes)) {
      return (
        <div style={{ marginBottom: '20px' }}>
          <Signposting callCenter={true}/>
        </div>
      )
    }

    const submitButton = (
      <Button
        bsStyle="primary"
        type="submit"
        className="medium-width pull-right secondary-button"
        disabled={submitting || (submitFailed && !valid) ||
          !valid || isLoading || disableOnNoChange}
      >
        {isLoading && (
          <FaSpinner className="fa-spin"/>
        )}
        CONTINUE
      </Button>
    );

    const goBackButton = (
      <Button
        bsStyle={'primary'}
        onClick={this.handleGoBack}
        className={'medium-width secondary-button'}>
        GO BACK
      </Button>
    )

    return (
      <Form horizontal={true} onSubmit={handleSubmit(this.handleSubmit)} autoComplete="off">
        <div className="admiral-card" style={{ marginTop: '20px' }}>
          <MTACardHeader
            text={'Update your level of cover below'}
            subtext={'Which level of cover do you need?'}
            icon={'legal-document'}
            border={true}
          />

          <div style={{ padding: '20px 0 20px 0' }}>
            {!isLoading && this.renderSchemeQuotes(selectedTripTypeQuotes, tripTypeName)}
          </div>
        </div>

        <>
          <div style={{ marginTop: '15px' }}/>
          <MTAButtons button1={goBackButton} button2={submitButton}/>
        </>

      </Form>
    );
  }

}

let selector;

export default flow([
  reduxForm({
    form: IMtaFormNames.COVER_DETAILS,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector(IMtaFormNames.COVER_DETAILS);
    return component;
  },
  connect(
    (state: IStore) => {
      const tripType = selector(state, 'data.attributes.metadata.scheme_type');
      const values = selector(
        state,
        'data.attributes',
      );
      return {
        mta: state.mta,
        selectedPolicy: state.mta.selectedPolicy,
        i18n: state.branding.i18n,
        formValues: values,
        tripType,
      };
    },
    (dispatch) => ({
      actions: {
        mta: bindActionCreators({ ...mtaActions }, dispatch),
        branding: bindActionCreators({ ...brandingActions }, dispatch),
        push: bindActionCreators(push, dispatch),
      },
    }),
  ),
])(MTAChangeTierForm);
